import { pcfRoutes } from '@app/pcf/pcf-routes';
import {
  IngredientResult,
  LinkedItem,
  LinkedItemTypeEnum,
  ProductResult,
} from '@shared/api/types';
import SimpleBarChart from '@shared/components/chart/simple-bar-chart';
import { Icons } from '@shared/components/content/icons';
import Text from '@shared/components/content/text';
import TextLink from '@shared/components/content/text-link';
import Unit from '@shared/components/content/unit';
import RatingCloud, {
  RatingCloudProps,
} from '@shared/components/ratings/rating-cloud';
import { ChartConfig } from '@shared/components/ui/chart';
import { Separator } from '@shared/components/ui/separator';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '@shared/components/ui/sheet';
import { cn, formatNumber } from '@shared/lib/utils';
import { generatePath, useNavigate } from 'react-router-dom';
import ProductMetric from '../components/product-metric';

interface ProductDetailIngredientSheetProps
  extends React.HTMLAttributes<HTMLDivElement> {
  product: ProductResult['name'];
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  ingredient: IngredientResult;
  totalEmissions: number;
}

export default function ProductDetailIngredientSheet({
  className,
  isOpen,
  setIsOpen,
  ingredient,
  product,
  totalEmissions,
  ...props
}: ProductDetailIngredientSheetProps) {
  const shareOfTotalEmissions = (ingredient.emissions / totalEmissions) * 100;
  const navigate = useNavigate();

  const stageChartData = [
    { stage: 'Farming', farming: ingredient.farmingEmissions },
    { stage: 'Processing', processing: ingredient.processingEmissions },
    { stage: 'Packaging', packaging: ingredient.packagingEmissions },
    { stage: 'Transport', transport: ingredient.transportEmissions },
  ];

  const stageChartConfig: ChartConfig = {
    emissions: { label: 'Emissions (kgCO2e)' },
    farming: { label: 'Farming', color: 'var(--farming-default)' },
    processing: { label: 'Processing', color: 'var(--processing-default)' },
    packaging: { label: 'Packaging', color: 'var(--packaging-default)' },
    transport: { label: 'Transport', color: 'var(--transport-default)' },
  };

  const handleLinkClick = (linkedItem: LinkedItem) => {
    switch (linkedItem.type) {
      case LinkedItemTypeEnum.product:
        navigate(
          generatePath(pcfRoutes.PRODUCT_DETAIL, {
            product_id: linkedItem.uuid,
          })
        );
        break;
      case LinkedItemTypeEnum.ingredient:
        navigate(
          generatePath(pcfRoutes.PRODUCT_COMPONENTS_INGREDIENTS_DETAIL, {
            ingredient_uuid: linkedItem.uuid,
          })
        );
        break;
      case LinkedItemTypeEnum.purchased_good:
        navigate(
          generatePath('/purchased-goods/food-and-drink/:item_uuid', {
            item_uuid: linkedItem.activityUuid!,
          })
        );
        break;
      default:
        break;
    }
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent
        className={cn('flex h-full flex-col', className)}
        {...props}
      >
        <SheetHeader className="-mt-1 space-y-0 border-b pb-4">
          <Text>{product}</Text>
          <SheetTitle className="text-xl font-semibold">
            {ingredient.longDesc}
          </SheetTitle>
        </SheetHeader>
        <div className="flex-1 space-y-3 overflow-y-auto">
          <div className="flex">
            <ProductMetric
              title="Ingredient emissions"
              className="flex-1 space-y-0"
              value={formatNumber(ingredient.emissions)}
              unit="kgCO2e"
              valueClassName="text-2xl"
            />
            <ProductMetric
              title="Share of total emissions"
              className="flex-1 space-y-0"
              value={formatNumber(shareOfTotalEmissions)}
              unit="%"
              valueClassName="text-2xl"
            />
          </div>

          <Separator />
          <div className="space-y-3">
            <div className="space-y-2">
              <Text variant="subSection">Ingredient modelling</Text>
              <Text className="text-sm font-light text-muted-foreground">
                This ingredient is linked to another item in your account.
                Follow the link below to review or update the modelling of that
                item.
              </Text>
            </div>
            <div>
              <Text className="font-semibold">Linked to</Text>
              <TextLink
                onClick={() => {
                  setIsOpen(false);
                  handleLinkClick(ingredient.linkedItem!);
                }}
                className="flex items-center gap-1 text-sm text-teal-600"
              >
                {ingredient.linkedItem?.type === LinkedItemTypeEnum.product && (
                  <Icons.salad size={16} />
                )}
                {ingredient.linkedItem?.type ===
                  LinkedItemTypeEnum.ingredient && <Icons.carrot size={16} />}
                {ingredient.linkedItem?.type ===
                  LinkedItemTypeEnum.purchased_good && (
                  <Icons.shoppingCart size={16} />
                )}
                {ingredient.linkedItem?.name as string}
              </TextLink>
            </div>
            <div>
              <Text className="font-semibold">Modelled by</Text>
              <Text> {ingredient.linkedItem?.modelledBy?.name as string}</Text>
            </div>
            <div className="pb-1">
              <Text className="font-semibold">Carbon intensity</Text>
              <div className="flex items-end gap-2">
                <RatingCloud
                  rating={
                    ingredient.emissionsCategory as RatingCloudProps['rating']
                  }
                  className="w-14"
                />
                <span className="flex items-baseline gap-x-1">
                  <Text className="text-2xl font-normal text-primary-600">
                    {formatNumber(ingredient.totalEmissionsPerKg!)}
                  </Text>
                  <Unit variant="kgCO2e/kg" className="text-sm" />
                </span>
              </div>{' '}
            </div>
            <Separator />

            <div>
              <Text variant="subSection" className="mb-2">
                Emissions by lifecycle stage
              </Text>
              <SimpleBarChart
                nameKey="stage"
                dataKey="farming"
                chartData={stageChartData}
                chartConfig={stageChartConfig}
                layout="vertical"
                CartesianGridProps={{ horizontal: false }}
                className="h-56 w-full"
                barSize={40}
                YAxisProps={{ width: 80, axisLine: false, tickLine: false }}
                margin={{ bottom: 20 }}
                XAxisProps={{
                  orientation: 'bottom',
                  axisLine: false,
                  label: {
                    value: 'Emissions (kgCO2e)',
                    position: 'insideBottom',
                    offset: -10,
                  },
                }}
              />
            </div>
          </div>
        </div>

        <div className="shrink-0 pt-3">
          <Text className="text-xs text-muted-foreground">
            This information is based on a snapshot of the data at the time of
            the last assessment.
          </Text>
        </div>
      </SheetContent>
    </Sheet>
  );
}
